import type { ReactNode } from "react";

interface FieldProps {
  label: string;
  /** Id of the control the label points at. */
  htmlFor: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
}

/**
 * A labelled form field with hint and error slots.
 *
 * Args:
 *     label: Visible label.
 *     htmlFor: Id of the labelled control.
 *     hint: Optional helper text.
 *     error: Optional validation message.
 *     children: The control itself.
 *
 * Returns:
 *     The field wrapper.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: FieldProps): JSX.Element {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="field__error" id={`${htmlFor}-error`} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="field__hint" id={`${htmlFor}-hint`}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
